import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { signIn } from '@/services/api/auth';
import { useAuth } from '@/contexts/AuthContext';
import { BrandMark } from '@/components/common/BrandMark';
import { cn } from '@/lib/cn';

export function LoginPage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && session) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const res = await signIn(email, password);
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.error.message);
      return;
    }
    toast.success('¡Bienvenido! Todo en orden. 👋');
    // La redirección post-login la maneja el guard al hidratar el profile
    navigate('/', { replace: true });
  }

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden px-6 py-10">
      {/* Shapes decorativos flotantes (inspiración papelería MANAXER) */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="brand-shape brand-shape-sky animate-float-a"
          style={{ top: '8%', left: '6%', width: 180, height: 220, opacity: 0.55 }}
        />
        <div
          className="brand-shape brand-shape-blue animate-float-b"
          style={{ bottom: '10%', right: '8%', width: 220, height: 260, opacity: 0.5 }}
        />
        <div
          className="brand-shape brand-shape-gray animate-float-c"
          style={{ top: '55%', left: '12%', width: 110, height: 140, opacity: 0.7 }}
        />
        <div
          className="brand-shape brand-shape-orange animate-float-a"
          style={{
            top: '18%',
            right: '14%',
            width: 52,
            height: 52,
            borderRadius: '18%',
            opacity: 0.8,
          }}
        />
        <div
          className="brand-shape brand-shape-sky animate-float-c"
          style={{ bottom: '22%', left: '38%', width: 80, height: 100, opacity: 0.45 }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/*
         * El PNG oficial tiene más aire a la derecha (el wordmark pesa visualmente
         * más que el isotipo), así que el flex justify-center deja el conjunto
         * "corrido" hacia la derecha. Compensamos con un pequeño shift perceptual.
         */}
        <div className="mb-8 flex justify-center">
          <BrandMark size={103} className="-translate-x-3" />
        </div>

        <div className="card-premium overflow-hidden">
          <div className="border-b border-surface-line bg-gradient-to-br from-blue/[0.03] to-transparent px-8 pb-6 pt-8">
            <p className="kicker mb-2">Ingreso</p>
            <h1 className="text-2xl font-extrabold tracking-tight text-neutral-ink">
              Entrá a tu panel
            </h1>
            <p className="mt-1 text-sm text-neutral-muted">Tu gestión. Todo en orden.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5 px-8 pb-8 pt-6">
            <Field
              label="Email"
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={setEmail}
              placeholder="pabloeacu@gmail.com"
              disabled={submitting}
            />
            <Field
              label="Contraseña"
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={setPassword}
              placeholder="••••••••"
              disabled={submitting}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="grid h-full w-10 place-items-center text-neutral-muted hover:text-blue"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />

            <button
              type="submit"
              disabled={submitting || !email || !password}
              className={cn(
                'mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-blue px-4 py-3',
                'font-semibold text-white shadow-premium-soft',
                'transition hover:brightness-110 active:translate-y-px',
                'disabled:pointer-events-none disabled:opacity-60',
              )}
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Ingresando…</span>
                </>
              ) : (
                <>
                  <LogIn size={18} />
                  <span>Ingresar</span>
                </>
              )}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-neutral-muted">
          MANAXER · División estratégica de Administración Virtual
        </p>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  id: string;
  type: string;
  required?: boolean;
  autoComplete?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rightSlot?: React.ReactNode;
}

function Field({
  label,
  id,
  type,
  required,
  autoComplete,
  value,
  onChange,
  placeholder,
  disabled,
  rightSlot,
}: FieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-muted"
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          name={id}
          type={type}
          required={required}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full rounded-md border border-surface-line bg-white px-3.5 py-2.5',
            'text-[15px] text-neutral-ink placeholder:text-neutral-muted/60',
            'transition focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue-30',
            'disabled:opacity-60',
            rightSlot && 'pr-11',
          )}
        />
        {rightSlot && <div className="absolute inset-y-0 right-0 flex">{rightSlot}</div>}
      </div>
    </div>
  );
}
